import { useEffect, useRef, useState } from 'react';
import type { ColDef } from 'ag-grid-community';
import { useDismissOnOutsideClick } from '../hooks/useDismissOnOutsideClick';

export interface SortItem {
  colId: string;
  sort: 'asc' | 'desc';
}
// ag-Grid text filter model, keyed by colId. Combined (two-condition) filters have
// `operator` + `condition1/2` instead of a top-level `filter`.
export type FilterModel = Record<string, { type?: string; filter?: string; operator?: string }>;

interface Props {
  columnDefs: ColDef[];
  sortModel: SortItem[];
  filterModel: FilterModel;
  onSortChange: (model: SortItem[]) => void;
  onFilterChange: (model: FilterModel) => void;
}

/**
 * List-view summary of the grid's sort + column filters, with per-column edit/remove —
 * the React port of dataGrid.ts's ListView sort/filter button bar.
 */
export function GridSortFilterBar({ columnDefs, sortModel, filterModel, onSortChange, onFilterChange }: Props) {
  const filterIds = Object.keys(filterModel);
  if (sortModel.length === 0 && filterIds.length === 0) return null;

  const headerName = (colId: string) =>
    columnDefs.find((c) => (c.colId ?? c.field) === colId)?.headerName ?? colId;

  const setSort = (colId: string, sort: 'asc' | 'desc') =>
    onSortChange(sortModel.map((s) => (s.colId === colId ? { ...s, sort } : s)));
  const removeSort = (colId: string) => onSortChange(sortModel.filter((s) => s.colId !== colId));
  const removeFilter = (colId: string) => {
    const { [colId]: _removed, ...rest } = filterModel;
    onFilterChange(rest);
  };

  return (
    <div className="resource-grid-sort-filter-bar">
      {sortModel.map((s) => (
        <ChipMenu
          key={`sort-${s.colId}`}
          label={`${headerName(s.colId)}: ${s.sort === 'asc' ? 'ascending' : 'descending'}`}
        >
          {(close) => (
            <>
              <li><a href="#" onMouseDown={(e) => { e.preventDefault(); setSort(s.colId, 'asc'); close(); }}>Ascending</a></li>
              <li><a href="#" onMouseDown={(e) => { e.preventDefault(); setSort(s.colId, 'desc'); close(); }}>Descending</a></li>
              <li><a href="#" onMouseDown={(e) => { e.preventDefault(); removeSort(s.colId); close(); }}>Remove</a></li>
            </>
          )}
        </ChipMenu>
      ))}
      {filterIds.map((colId) => (
        <ChipMenu key={`filter-${colId}`} label={<>{headerName(colId)} <i className="fas fa-filter" /></>}>
          {(close) => (
            <>
              {/* Combined filters have no single text value; they can only be removed here. */}
              {!filterModel[colId].operator && (
                <li style={{ padding: 5 }}>
                  {/* Cleared text keeps the chip (matches no constraint); Remove drops it. */}
                  <FilterTextInput
                    value={filterModel[colId].filter ?? ''}
                    onCommit={(text) => onFilterChange({ ...filterModel, [colId]: { ...filterModel[colId], filter: text } })}
                  />
                </li>
              )}
              <li><a href="#" onMouseDown={(e) => { e.preventDefault(); removeFilter(colId); close(); }}>Remove</a></li>
            </>
          )}
        </ChipMenu>
      ))}
    </div>
  );
}

function ChipMenu({ label, children }: { label: React.ReactNode; children: (close: () => void) => React.ReactNode }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useDismissOnOutsideClick(ref, open, () => setOpen(false));
  return (
    <div className="btn-group" ref={ref}>
      <button type="button" className="btn btn-default dropdown-toggle" onClick={() => setOpen((v) => !v)}>
        {label} <span className="caret" />
      </button>
      {open && (
        <ul className="dropdown-menu" style={{ display: 'block' }}>
          {children(() => setOpen(false))}
        </ul>
      )}
    </div>
  );
}

// Refetches as the user types, debounced like the tag-suggestion inputs (MsComboBox).
// A pending edit is flushed if the menu closes first.
function FilterTextInput({ value, onCommit }: { value: string; onCommit: (text: string) => void }) {
  const [draft, setDraft] = useState(value);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingRef = useRef<(() => void) | null>(null);

  useEffect(() => () => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    pendingRef.current?.();
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const text = e.target.value;
    setDraft(text);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    pendingRef.current = () => { pendingRef.current = null; onCommit(text); };
    debounceRef.current = setTimeout(() => pendingRef.current?.(), 300);
  };

  return (
    <input
      type="text"
      className="form-control input-sm"
      aria-label="Filter text"
      value={draft}
      autoFocus
      onChange={handleChange}
    />
  );
}
